import { makePrivateRequestAsRole, makePrivateRequestAsUser, makePublicRequest } from './managers/requestBuilder';

import { roundTo2dp } from '../utils/maths.new';
import { ANALYTICS_TYPES } from '../utils/cameras';
import { splitIntoChunks } from '../utils/requestHelpers';
import { getRegionForOrg, getRegionForRequest } from './managers/store';

/**
 * @typedef VehicleImageCoordinates
 * @type {object}
 * @property {number} x1 X coordinate of left edge between 0 and 100 inclusive.
 * @property {number} y1 Y coordinate of top edge between 0 and 100 inclusive.
 * @property {number} x2 X coordinate of right edge between 0 and 100 inclusive.
 * @property {number} y2 Y coordinate of bottom edge between 0 and 100 inclusive.
 */

/**
 * @typedef PlateDetection
 * @type {object}
 * @property {string} uidd Uidd of camera which recorded the vehicle.
 * @property {number} uid Owner of camera which recorded the vehicle.
 * @property {number} deviceId Device id of camera which recorded the vehicle.
 * @property {number} [eventId] Event id (alert id) which this detections falls within.
 * @property {string} vehicleId Uuid assigned to vehicle.
 * @property {string} [licencePlate] Licence plate of vehicle.
 * @property {string} [licencePlateRegion] Licence plate region.
 * @property {VehicleImageCoordinates} [licencePlateCoordinates] Bounding box of licence plate within image for this detection.
 * @property {VehicleImageCoordinates} [vehicleCoordinates] Bounding box of vehicle within image for this detection.
 * @property {number} [confidence] Confidence level of licence plate reading between 0 and 100 inclusive.
 * @property {number} [mmcConfidence] Confidence level of make/model/colour detection between 0 and 100 inclusive.
 * @property {number} stillTimeMs Unix timestamp of detection.
 * @property {string} [make] Vehicle manufacturer.
 * @property {string} [model] Vehicle model.
 * @property {string} [colour] Colour of vehicle.
 * @property {('f', 'r', 'u')} [plateLocation] Location of plate: front, rear or unknown.
 * @property {number} [score] When searching for a plate, a "score" will be returned from Atlas search.
 * @property {('videoloft', 'TVT')} provider Who did the plate detection?
 * @property {number} startTime Unix timestamp of when vehicle appears on camera (use for video).
 * @property {number} endTime Unix timestamp of when vehicle leaves camera view (use for video).
 * @property {number} firstSeen Unix timestamp of when vehicle first appeared on any camera owned by the owner of this camera.
 */

/**
 * @typedef VehicleMakeModel
 * @type {object}
 * @property {string} make Vehicle manufacturer.
 * @property {string} model Vehicle model.
 */

/**
 * Search for licence plate detections matching specified filters.
 * 
 * TODO: We need a separate version of this API that just returns raw data without the `map` formatting it.
 * This is for use by CSV downloads and would work the same as the equivalent functions for PPE and people
 * counting.
 * 
 * @param {string[]} cameraUidds Cameras to search across.
 * @param {number} startTime Start of time range to search across.
 * @param {object} [options] Additional options.
 * @param {number} [options.endTime] End of time range to search across.
 * @param {string|string[]} [options.licencePlates] Licence plate(s) to search for. Can include wildcard characters.
 * @param {string} [options.make] Vehicle manufacturer.
 * @param {string} [options.model] Vehicle model.
 * @param {string} [options.colour] Vehicle colour.
 * @param {boolean} [options.ascendingOrder] Sort results by ascending order of time.
 * @param {number} [options.limit] Max number of results to return.
 * @param {string} [options.searchAfter] Pagination token. Returns results after this document. Only available with plate searches.
 * @param {boolean} [options.searchWithCount] Return total search results too. Only available with plate searches.
 * @param {function} [options.onDownloadProgress] `onDownloadProgress` callback to pass to Axios.
 * @param {AbortSignal} [options.abortSignal] Abort signal to cancel the request.
 * @param {AbortSignal} [organisations]
 * @returns {Promise<PlateDetection[]>} Plate detections matching filters.
 */
export const getLicencePlates = makePrivateRequestAsUser(
    async (
        { makePrivateRequest },
        cameraUidds,
        startTime,
        {
            endTime,
            licencePlates,
            make,
            model,
            colour,
            ascendingOrder = false,
            limit = 50,
            searchAfter,
            searchWithCount = false,
            onDownloadProgress,
            abortSignal
        } = {}
    ) => {
        const { data } = await makePrivateRequest(
            'analytics',
            'POST',
            '/vehicles',
            {
                splitByRegion: true,
                data: {
                    uidds: cameraUidds,
                    startTime,
                    endTime,
                    licencePlates: licencePlates?.length > 0 ? licencePlates : undefined,
                    make: make?.length > 0 ? make : undefined,
                    model: model?.length > 0 ? model : undefined,
                    colour: colour?.length > 0 ? colour : undefined,
                    ascendingOrder,
                    searchAfter,
                    searchWithCount,
                    limit
                },
                signal: abortSignal,
                onDownloadProgress
            }
        );

        const multipleRegions = new Set(cameraUidds.map(uidd => getRegionForOrg(uidd.split('.')[0]))).size > 1;

        const mapped = data.map(({ uid, deviceId, firstSeen, startTime, endTime, ...plate }) => ({
            uid,
            deviceId,
            uidd: `${uid}.${deviceId}`,
            firstSeen: firstSeen ? firstSeen * 1000 : undefined,
            startTime: startTime ? startTime * 1000 : undefined,
            endTime: endTime ? endTime * 1000 : undefined,
            ...plate
        }));

        return [
            multipleRegions
                ? mapped.sort((a, b) => ascendingOrder
                    ? (a.startTime ?? 0) - (b.startTime ?? 0)
                    : (b.startTime ?? 0) - (a.startTime ?? 0)
                )
                : mapped,
            data.length < limit
        ];
    }
);

/**
 * Get total sightings for each of the specified licence plates between the given time range and on the specified cameras.
 * @param {string[]} cameraUidds Cameras to search across.
 * @param {string[]} licencePlates A count will be returned for each of these licence plates. Exact plates only.
 * @param {number} startTime Start of time range to search across.
 * @param {object} [options] Additional options.
 * @param {number} [options.endTime] End of time range to search across.
 * @param {AbortSignal} [options.abortSignal] Abort signal to cancel the request.
 * @returns {Promise<object<string, number>>} Object mapping licence plate to count.
 */
export const getCountPerLicencePlate = makePrivateRequestAsUser(
    async (
        { makePrivateRequest },
        cameraUidds,
        licencePlates,
        startTime,
        {
            endTime,
            abortSignal
        }
    ) => {

        // Backend limits number of plates per request
        const MAX_PER_QUERY = 300;

        const results = await Promise.all(
            // Split array into groups of max limit and make request for each
            Array(Math.ceil(licencePlates.length / MAX_PER_QUERY)).fill(0).map((e,i) => licencePlates.slice(i * MAX_PER_QUERY, MAX_PER_QUERY * (i + 1))).map(plates => {
                return makePrivateRequest(
                    'analytics',
                    'POST',
                    '/vehicles/plate-sightings',
                    {
                        data: {
                            uidds: cameraUidds,
                            startTime,
                            endTime,
                            licencePlates: plates,
                        },
                        signal: abortSignal,
                        splitByRegion: true
                    }
                );
            })
        );

        // Combine each set of results
        return results.reduce((acc, { data }) => ({
            ...acc,
            // Combine counts within each set
            ...data.reduce((acc, { licencePlate, count }) => ({
                ...acc,
                [licencePlate]: count
            }), {})
        }), {});
    }
);

/**
 * Total count of all plate detections meeting specified filters.
 * @param {string[]} cameraUidds Cameras to search across.
 * @param {number} startTime Start of time range to search across.
 * @param {object} [options] Additional options.
 * @param {number} [options.endTime] End of time range to search across.
 * @param {string} [options.make] Vehicle manufacturer.
 * @param {string} [options.model] Vehicle model.
 * @param {string} [options.colour] Vehicle colour.
 * @param {AbortSignal} [options.abortSignal] Abort signal to cancel the request.
 * @returns {Promise<number>} Total count.
 */
export const getTotalLicencePlatesCount = makePrivateRequestAsUser(
    async (
        { makePrivateRequest },
        cameraUidds,
        startTime,
        {
            endTime,
            make,
            model,
            colour,
            abortSignal
        }
    ) => {

        const { data } = await makePrivateRequest(
            'analytics',
            'POST',
            '/vehicles/count',
            {
                data: {
                    uidds: cameraUidds,
                    startTime,
                    endTime,
                    make: make?.length > 0 ? make : undefined,
                    model: model?.length > 0 ? model : undefined,
                    colour: colour?.length > 0 ? colour : undefined,
                },
                signal: abortSignal,
                splitByRegion: true
            }
        );

        return data.count;
    }
);

/**
 * @param {string} cameraUidd Uidd of camera which recorded the vehicle.
 * @param {number} time Timestamp of when image was taken.
 * @param {string} vehicleId Id of vehicle.
 * @param {boolean} [plateImage] Use to get licence plate image for TVT detections.
 */
export const getVehicleImage = makePrivateRequestAsUser(async ({ token }, cameraUidd, time, vehicleId, plateImage = false) => {

    const [uid, deviceId] = cameraUidd.split('.');

    const { data } = await makePublicRequest(
        'video',
        'GET',
        `/images/lpr/${uid}/${deviceId}/${time}/${vehicleId}?token=${token}${plateImage ? '&suffix=_crop' : ''}`,
        {
            responseType: 'blob',
            region: getRegionForRequest(uid)
        }
    );

    return data;
});

/**
 * Fetch all possible vehicle makes and models that we have stored.
 * @returns {Promise<VehicleMakeModel[]>} All vehicles.
 */
export const getAllVehicleMakesModels = makePrivateRequestAsUser(
    async (
        { makePrivateRequest }
    ) => {

        const { data } = await makePrivateRequest(
            'analytics',
            'GET',
            '/vehicles/models'
        );

        return data;
    }
);

export const getVehicleCounts = makePrivateRequestAsUser(async ({ makePrivateRequest, region }, logger, cameraUidds, startTime, endTime) => {

    const { data } = await makePrivateRequest(
        region === 'uswest2' ? 'uswest2-moc-logger-1699287773-i-0cd7170254efe0df0.manything.com' : logger,
        'GET',
        `/vehicles`,
        {
            params: {
                uidd: cameraUidds,
                startTime,
                endTime
            }
        }
    );

    return data.map(({ uid, deviceid, total_day, total_night, plates_read_day, plates_read_night }) => ({
        uid,
        deviceId: deviceid,
        uidd: `${uid}.${deviceid}`,
        platesReadTotal: plates_read_day + plates_read_night,
        platesReadDay: plates_read_day,
        platesReadNight: plates_read_night,
        total: total_day + total_night,
        totalDay: total_day,
        totalNight: total_night
    }));
});


/* ------------------------------ Camera config ----------------------------- */

/**
 * @typedef AnalyticsArea
 * @type {object}
 * @property {number} id Area id.
 * @property {number} uid Camera owner.
 * @property {number} deviceId Camera id.
 * @property {number[][]} coordinates Array of [x,y] nodes of polygon. Coordinates are percentages between 0 and 1 inclusive. E.g. [[0.3, 0.4], [0.3, 0.5], [0.71, 0.85]].
 * @property {string} [name] Name of area assigned by user.
 * @property {string} [description] User description of area.
 * @property {('in', 'out')} [direction] If applicable, which direction does this area cover. E.g. roads going into and out of Yosemite.
 */

/**
 * Get list of analytics polygons set up for a camera.
 * @param {string} uidd Camera uidd.
 * @returns {AnalyticsArea[]} Array of areas.
 */
export const getCameraAnalyticsAreas = makePrivateRequestAsUser(async ({ makePrivateRequest }, uidd) => {
    const [uid, deviceId] = uidd.split('.');
    
    const { data } = await makePrivateRequest(
        'auth',
        'GET',
        `/camera/${uid}/${deviceId}/analytics/areas`,
        {
            region: getRegionForRequest(uid)
        }
    );

    return data.map(({ coordinates, ...polygon }) => ({
        ...polygon,
        coordinates: coordinates.map(([x, y]) => [x / 100, y / 100]),
        scheme: ANALYTICS_TYPES.LPR // HARDCODED SCHEME FOR LPR, there is no other scheme using areas for now..
    }));
});

/**
 * Get list of analytics polygons set up for one or more cameras.
 * @param {string} uidds Cameras uidds.
 * @returns {AnalyticsArea[]} Array of areas.
 */
 const getCamerasAnalyticsAreasRequest = async ({ makePrivateRequest }, uidds) => {
    const splitUidds = splitIntoChunks(uidds);

    // Make API requests for each subarray
    const requests = splitUidds.map(async (uidd) => {
        const { data } = await makePrivateRequest(
            'auth',
            'GET',
            `/analytics/areas`,
            {
                splitByRegion: true,
                params: {
                    uidd: uidd,
                }
            }
        );
        return data;
    });
    return Promise.all(requests);
};
export const getCamerasAnalyticsAreas = (uidds, isManager = false) => makePrivateRequestAsRole(isManager)(getCamerasAnalyticsAreasRequest)(uidds);

/**
 * Save new area for a camera.
 * @param {string} uidd Camera uidd.
 * @param {number[][]} coordinates Polygon nodes. Each x and y should be between 0 and 1 inclusive.
 * @param {object} [details] Extra details.
 * @param {string} [details.name] Area name.
 * @param {string} [details.description] Area description.
 * @param {('in', 'out')} [details.direction] Direction the area represents.
 * @param {AbortSignal} [abortSignal] AbortSignal to pass to Axios.
 * @returns {AnalyticsArea} Newly created area.
 */
export const createCameraAnalyticsArea = makePrivateRequestAsUser(async ({ makePrivateRequest }, uidd, coordinates, { name, description, direction } = {}, abortSignal) => {
    const [uid, deviceId] = uidd.split('.');
    
    const { data } = await makePrivateRequest(
        'auth',
        'POST',
        `/camera/${uid}/${deviceId}/analytics/areas`,
        {
            data: {
                coordinates: coordinates.map(([x, y]) => [roundTo2dp(x * 100), roundTo2dp(y * 100)]),
                name: name || undefined,
                description: description || undefined,
                direction: direction || undefined
            },
            signal: abortSignal,
            region: getRegionForRequest(uid)
        }
    );

    return data;
});

/**
 * Update a saved area for a camera.
 * @param {string} uidd Camera uidd.
 * @param {number} areaId Id of area.
 * @param {number[][]} coordinates Polygon nodes.
 * @param {object} [details] Extra details.
 * @param {string} [details.name] Area name.
 * @param {string} [details.description] Area description.
 * @param {('in', 'out')} [details.direction] Direction the area represents.
 * @param {AbortSignal} [abortSignal] AbortSignal to pass to Axios.
 * @returns Axios promise.
 */
export const updateCameraAnalyticsArea = makePrivateRequestAsUser(async ({ makePrivateRequest }, uidd, areaId, coordinates, { name, description, direction } = {}, abortSignal) => {
    const [uid, deviceId] = uidd.split('.');
    
    const { data } = await makePrivateRequest(
        'auth',
        'PUT',
        `/camera/${uid}/${deviceId}/analytics/areas/${areaId}`,
        {
            data: {
                coordinates: coordinates.map(([x, y]) => [roundTo2dp(x * 100), roundTo2dp(y * 100)]),
                name: name || undefined,
                description: description || undefined,
                direction: direction || undefined
            },
            signal: abortSignal,
            region: getRegionForRequest(uid)
        }
    );

    return data;
});

/**
 * Delete a saved area for a camera.
 * @param {string} uidd Camera uidd.
 * @param {number} areaId Id of area.
 * @param {AbortSignal} [abortSignal] AbortSignal to pass to Axios.
 * @returns Axios promise.
 */
export const deleteCameraAnalyticsArea = makePrivateRequestAsUser(async ({ makePrivateRequest }, uidd, areaId, abortSignal) => {
    const [uid, deviceId] = uidd.split('.');
    
    const { data } = await makePrivateRequest(
        'auth',
        'DELETE',
        `/camera/${uid}/${deviceId}/analytics/areas/${areaId}`,
        {
            signal: abortSignal,
            region: getRegionForRequest(uid)
        }
    );

    return data;
});